import { cloneDeep } from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch } from '@/store'
import Api from '@/services/http'
import * as Types from './type'

const initialState: Types.HomeType = {
  downloadInfo: {
    pageNum: 1,
    hasNext: true,
    data: [],
    socketData: [],
  },
  messageInfo: {
    pageNum: 1,
    hasNext: true,
    data: [],
    socketData: [],
  },
  // 目前只有一个自定义打印面对的异步事件，先以taskId为key存储所有taskResults，taskId全局唯一
  // 后续如果一个页面需要存在多个异步事件需要接受处理的话，再将每个异步事件独立开
  taskResultsMap: {},
  // socket连接失败时补偿接口需要用到
  taskIdCaches: [],
  // dashboard首页通知
  hasNotifyCount: false,
  unReadMessageCount: 0,
  unReadDownloadCount: 0,
}

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setDownloadList(state, action: PayloadAction<Types.ResponseDownloadListType>) {
      const { pageNum, data, hasNext } = action.payload
      const list = pageNum >= 2 ? state.downloadInfo.data.concat(data) : data
      return {
        ...state,
        downloadInfo: {
          ...state.downloadInfo,
          pageNum,
          hasNext,
          data: list,
        },
      }
    },
    setMessageList(state, action: PayloadAction<Types.ResponseMessageListType>) {
      const { pageNum, data, hasNext } = action.payload
      const list = pageNum >= 2 ? state.messageInfo.data.concat(data) : data
      return {
        ...state,
        messageInfo: {
          ...state.messageInfo,
          pageNum,
          hasNext,
          data: list,
        },
      }
    },
    addTaskResultsItem(state, action: PayloadAction<Types.ITaskResultProps>) {
      const taskResultsMap = Object.assign({}, state.taskResultsMap, { [action.payload.taskId]: action.payload })
      return {
        ...state,
        taskResultsMap,
      }
    },
    deleteTaskId(state, action: PayloadAction<{ taskId: string }>) {
      const taskIdCaches = state.taskIdCaches.filter((taskId) => taskId !== action.payload.taskId)
      return {
        ...state,
        taskIdCaches,
      }
    },
    deleteTaskResultsItem(state, action: PayloadAction<{ taskId: string }>) {
      const taskResultsMap = { ...state.taskResultsMap }
      delete taskResultsMap[action.payload.taskId]
      return {
        ...state,
        taskResultsMap,
      }
    },
    setUnReadMessageCount(state, action: PayloadAction<{ messageCount: number }>) {
      const messageCount = action.payload.messageCount
      if (messageCount === null || messageCount === undefined) return state
      return {
        ...state,
        unReadMessageCount: Number(messageCount),
      }
    },
    setUnReadDownloadCount(state, action: PayloadAction<{ exportCount: number }>) {
      const exportCount = action.payload.exportCount
      if (exportCount === null || exportCount === undefined) return state
      return {
        ...state,
        unReadDownloadCount: Number(exportCount),
      }
    },
    setSocketMessageList(state, action: PayloadAction<Types.MessageItemType[]>) {
      const data = action.payload
      const list = data.length ? [...data, ...state.messageInfo.socketData] : data
      return {
        ...state,
        messageInfo: {
          ...state.messageInfo,
          socketData: list,
        },
      }
    },
    setHasNotifyCount(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        hasNotifyCount: action.payload,
      }
    },
    setSocketDownloadList(state, action: PayloadAction<Types.DownloadItemType[]>) {
      const data = action.payload
      const list = data.length ? [...data, ...state.downloadInfo.socketData] : data
      return {
        ...state,
        downloadInfo: {
          ...state.downloadInfo,
          socketData: list,
        },
      }
    },
    setMessageRead(state, action: PayloadAction<Types.MessageReadType>) {
      const { messageInfo, unReadMessageCount } = state
      const { msgSeq } = action.payload
      // 没有msgSeq则为全部已读
      const newMessageList = messageInfo.data.map((item: Types.MessageItemType) => {
        if (!msgSeq || item.msgSeq === msgSeq) {
          const newItem = cloneDeep(item)
          newItem.read = true
          return newItem
        }
        return item
      })

      return {
        ...state,
        messageInfo: {
          ...state.messageInfo,
          data: newMessageList,
        },
        unReadMessageCount: unReadMessageCount - 1,
      }
    },
    setDownloadRead(state, action: PayloadAction<Types.DownloadReadType>) {
      const { downloadInfo, unReadDownloadCount } = state
      const newDownloadList = downloadInfo.data.map((item) => {
        if (item.taskId === action.payload.taskId) {
          const newItem = cloneDeep(item)
          newItem.read = true
          return newItem
        }
        return item
      })
      return {
        ...state,
        downloadInfo: {
          ...state.downloadInfo,
          data: newDownloadList,
        },
        unReadDownloadCount: unReadDownloadCount - 1,
      }
    },
  },
})

export const homeActions = homeSlice.actions

/** 导出文件列表 */
export const queryDownloadList =
  (props: { pageNum?: number; pageSize?: number } = {}) =>
  async (dispatch: AppDispatch) => {
    const { pageNum = 1, pageSize = 100 } = props
    const response = await Api.admin.post<Types.ResponseDownloadListType>('/api/order/export/query', { pageNum, pageSize })
    const { data: rstData, code } = response
    if (code === 'SUCCESS') {
      const { pageNum, hasNext, data: listData } = rstData
      dispatch(homeActions.setDownloadList({ pageNum, hasNext, data: listData }))
    }
  }

export const queryMessageList =
  (props: { pageNum?: number; pageSize?: number } = {}) =>
  async (dispatch: AppDispatch) => {
    const { pageNum = 1, pageSize = 100 } = props
    const response = await Api.admin.post<Types.ResponseMessageListType>('/api/user/message/list', { pageNum, pageSize })
    const { data: rstData, code } = response
    if (code === 'SUCCESS') {
      const { pageNum, hasNext, data: listData } = rstData
      // @deprecated 过度因为旧数据仍然含有 oneshipNo，因此这里需要做一下兼容
      listData.forEach((item) => {
        const { extInfo } = item || {}
        if (typeof extInfo === 'object' && extInfo !== null) {
          const { logisticsNo, oneshipNo } = extInfo
          extInfo.logisticsNo = logisticsNo || oneshipNo
        }
      })

      dispatch(homeActions.setMessageList({ pageNum, hasNext, data: listData }))
    }
  }

/** 已读消息 */
export const readMessage = (msgSeq?: string) => async (dispatch: AppDispatch) => {
  const response = await Api.admin.post('/api/user/message/read', { msgSeq })
  const { code } = response
  if (code === 'SUCCESS') {
    dispatch(homeActions.setMessageRead({ msgSeq }))
  }
}

/** 已读下载 */
export const readDownload = (taskId?: string) => async (dispatch: AppDispatch) => {
  const response = await Api.admin.post('/api/order/export/read', { taskId })
  const { code } = response
  if (code === 'SUCCESS' && taskId) {
    dispatch(homeActions.setDownloadRead({ taskId }))
  }
}

/** 获取s3下载地址 */
export const queryS3DownloadUrl = async (taskId: string, options: Record<string, any>) => {
  // return Api.admin.post<Types.ResS3DownloadType>('/api/order/export/list_export_file_url', { taskIds: [taskId] })
  return Api.admin.post<Types.NewDownloadUrlType>('/api/order/export/file', { taskId }, {...options})
}

export default homeSlice.reducer
